import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { LocaleCode } from "../../app/locale-store";
import type { ApiClient } from "../../platform/api-client";

export interface RuntimeTerminologyEntry {
  readonly id: string;
  readonly code: string;
  readonly termDe: string;
  readonly termEn: string | null;
  readonly definitionDe: string;
  readonly definitionEn: string | null;
  readonly context: string;
  readonly aliases: readonly string[];
  readonly version: number;
}

interface TerminologyRuntimeValue {
  readonly entries: readonly RuntimeTerminologyEntry[];
  readonly locale: LocaleCode;
  readonly reload: () => Promise<void>;
}

const CACHE_KEY = "thw.terminology.dictionary.v1";
const TerminologyRuntimeContext = createContext<TerminologyRuntimeValue | null>(null);

function cachedEntries(): readonly RuntimeTerminologyEntry[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(CACHE_KEY) ?? "[]") as unknown;
    return Array.isArray(parsed) ? parsed as RuntimeTerminologyEntry[] : [];
  } catch {
    return [];
  }
}

export function TerminologyProvider({
  api,
  children,
  locale,
}: {
  readonly api: ApiClient;
  readonly children: ReactNode;
  readonly locale: LocaleCode;
}): ReactNode {
  const [entries, setEntries] = useState<readonly RuntimeTerminologyEntry[]>(cachedEntries);

  async function reload(): Promise<void> {
    try {
      const result = await api.get<{ readonly entries: readonly RuntimeTerminologyEntry[] }>(
        "/v1/terminology/dictionary",
      );
      setEntries(result.entries);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(result.entries));
      } catch {
        // Storage is an optional offline enhancement; the in-memory dictionary remains usable.
      }
    } catch {
      // Tooltips are progressive assistance. A failed refresh keeps the last safe dictionary.
    }
  }

  useEffect(() => {
    void reload();
  }, [api]);

  const value = useMemo<TerminologyRuntimeValue>(
    () => ({ entries, locale, reload }),
    [entries, locale],
  );
  return (
    <TerminologyRuntimeContext.Provider value={value}>
      {children}
    </TerminologyRuntimeContext.Provider>
  );
}

export function useTerminologyRuntime(): TerminologyRuntimeValue {
  return useContext(TerminologyRuntimeContext) ?? {
    entries: [],
    locale: "de",
    reload: () => Promise.resolve(),
  };
}

function normalized(value: string): string {
  return value.normalize("NFKC").trim().replace(/\s+/gu, " ").toLocaleUpperCase("de-DE");
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function resolveEntries(
  entries: readonly RuntimeTerminologyEntry[],
  context: string | undefined,
): ReadonlyMap<string, RuntimeTerminologyEntry> {
  const candidates = new Map<string, Map<string, RuntimeTerminologyEntry>>();
  for (const entry of entries) {
    if (context !== undefined && entry.context !== context) continue;
    for (const token of [entry.code, ...entry.aliases]) {
      const key = normalized(token);
      const group = candidates.get(key) ?? new Map<string, RuntimeTerminologyEntry>();
      group.set(entry.id, entry);
      candidates.set(key, group);
    }
  }
  const resolved = new Map<string, RuntimeTerminologyEntry>();
  for (const [key, group] of candidates) {
    if (group.size === 1) {
      const entry = group.values().next().value as RuntimeTerminologyEntry;
      resolved.set(key, entry);
    }
  }
  return resolved;
}

function TerminologyToken({
  children,
  entry,
  locale,
}: {
  readonly children: string;
  readonly entry: RuntimeTerminologyEntry;
  readonly locale: LocaleCode;
}): ReactNode {
  const id = `${useId()}-terminology-tooltip`;
  const localizedTerm = locale === "en" ? entry.termEn ?? entry.termDe : entry.termDe;
  const localizedDefinition =
    locale === "en" ? entry.definitionEn ?? entry.definitionDe : entry.definitionDe;
  const definitionLanguage = locale === "en" && entry.definitionEn === null ? "de" : locale;
  return (
    <span className="thw-terminology-token">
      <abbr aria-describedby={id} tabIndex={0} title={localizedTerm}>
        {children}
      </abbr>
      <span className="thw-terminology-tooltip" id={id} role="tooltip">
        <strong>{entry.code} · {localizedTerm}</strong>
        <span lang={definitionLanguage}>{localizedDefinition}</span>
      </span>
    </span>
  );
}

export function AbbreviationText({
  children,
  context,
}: {
  readonly children: string;
  readonly context?: string;
}): ReactNode {
  const runtime = useTerminologyRuntime();
  const lookup = useMemo(
    () => resolveEntries(runtime.entries, context),
    [context, runtime.entries],
  );
  const pattern = useMemo(() => {
    const tokens = [...lookup.keys()].sort((left, right) => right.length - left.length);
    if (tokens.length === 0) return null;
    return new RegExp(
      `(?<![\\p{L}\\p{N}])(${tokens.map(escapeRegExp).join("|")})(?![\\p{L}\\p{N}])`,
      "giu",
    );
  }, [lookup]);
  if (pattern === null) return children;

  const result: ReactNode[] = [];
  let cursor = 0;
  for (const match of children.matchAll(pattern)) {
    const index = match.index;
    const token = match[0];
    if (index > cursor) result.push(children.slice(cursor, index));
    const entry = lookup.get(normalized(token));
    result.push(entry === undefined
      ? token
      : (
          <TerminologyToken entry={entry} key={`${String(index)}-${entry.id}`} locale={runtime.locale}>
            {token}
          </TerminologyToken>
        ));
    cursor = index + token.length;
  }
  if (cursor < children.length) result.push(children.slice(cursor));
  return <Fragment>{result}</Fragment>;
}
